import React, { FC, useMemo, useRef, useEffect, useCallback } from 'react';
import lottie, { AnimationItem } from 'lottie-web';
import { CardSchema } from 'constants/graphqlTypes';
import {
  calculateImagePosition,
  getImageSize,
  getResponseImageUrl,
  getRotationForCount,
  getShiftAmount,
  AspectRatioType,
} from './helpers';
import { StyledScatteredImages, StyledScatteredImage } from './styled';
import orbitAnimationData from '../../../../assets/lottie/scattered-images-orbit.json';

/**
 * Lottie layer structure (from Bg-images-example.json):
 *
 * Layer hierarchy per image (4 images total):
 *   Shape layer (visible rect) → orbit null (animated pos + rot) → base null (static pos + tilt) → root
 *
 * We read the computed SVG transform of each shape layer's <g> element on each frame
 * to extract the world-space position and rotation, then map to our container.
 *
 * Canvas: 656×508. Shape layers are ind: 4, 7, 10, 13.
 */
const LOTTIE_W = 656;
const LOTTIE_H = 508;

// The shape layer indices in the Lottie (the visible rectangles we track)
const SHAPE_LAYER_INDICES = [4, 7, 10, 13];

interface ScatteredImagesProps {
  responses: CardSchema[];
  maxImages?: number;
  isAnimating?: boolean;
}

interface ImageData {
  id: string;
  url: string;
  aspectRatioType: AspectRatioType;
}

const ScatteredImages: FC<ScatteredImagesProps> = ({ responses, maxImages = 4, isAnimating = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const lottieContainerRef = useRef<HTMLDivElement>(null);
  const animInstanceRef = useRef<AnimationItem | null>(null);
  const orbitActiveRef = useRef(false);

  // Baseline positions captured at animation start (first frame of Lottie)
  const baselineRef = useRef<{ x: number; y: number; rotation: number }[]>([]);

  // Extract image URLs from response cards
  const images = useMemo((): ImageData[] => {
    const result: ImageData[] = [];

    responses.slice(0, maxImages).forEach((response) => {
      const url = getResponseImageUrl(response);
      if (url) {
        result.push({
          id: response.id,
          url,
          aspectRatioType: 'square',
        });
      }
    });

    return result;
  }, [responses, maxImages]);

  // Read transforms from Lottie SVG and apply to image elements
  const handleEnterFrame = useCallback(() => {
    if (!orbitActiveRef.current) return;

    const lottieEl = lottieContainerRef.current;
    const container = containerRef.current;
    if (!lottieEl || !container) return;

    const containerW = container.offsetWidth;
    const containerH = container.offsetHeight;
    const scaleX = containerW / LOTTIE_W;
    const scaleY = containerH / LOTTIE_H;

    // lottie-web SVG renderer creates <g> elements for each layer
    // They are nested in reverse order. We query by the class lottie assigns.
    const svgEl = lottieEl.querySelector('svg');
    if (!svgEl) return;

    // lottie-web creates <g> elements with class "ai" for animated items
    // The shape layers get their own <g> with a transform attribute
    // We access them through the animation instance's renderer
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const renderer = (animInstanceRef.current as any)?.renderer;
    if (!renderer?.elements) return;

    const imageCount = Math.min(images.length, 4);

    Array.from({ length: imageCount }).forEach((_, i) => {
      const imgEl = imageRefs.current[i];
      if (!imgEl) return;

      const layerIdx = SHAPE_LAYER_INDICES[i];
      // Find the renderer element matching this layer index
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rendererEl = renderer.elements.find((el: any) => el?.data?.ind === layerIdx);
      if (!rendererEl?.layerElement) return;

      // Get the world-space transform by reading the final CTM of the layer's SVG group
      const layerGroup: SVGGElement = rendererEl.layerElement;
      const ctm = layerGroup.getCTM();
      if (!ctm) return;

      const worldX = ctm.e;
      const worldY = ctm.f;
      const worldRot = Math.atan2(ctm.b, ctm.a) * (180 / Math.PI);

      // Capture baseline on first frame
      if (baselineRef.current.length < imageCount) {
        baselineRef.current.push({ x: worldX, y: worldY, rotation: worldRot });
        return;
      }

      const baseline = baselineRef.current[i];
      if (!baseline) return;

      // Delta from baseline, scaled to our container
      const deltaX = (worldX - baseline.x) * scaleX;
      const deltaY = (worldY - baseline.y) * scaleY;
      const deltaRot = worldRot - baseline.rotation;

      imgEl.style.setProperty('--orbit-x', `${deltaX}px`);
      imgEl.style.setProperty('--orbit-y', `${deltaY}px`);
      imgEl.style.setProperty('--orbit-rot', `${deltaRot}deg`);
    });
  }, [images.length]);

  // Initialize lottie animation (off-screen)
  useEffect(() => {
    if (images.length === 0) return undefined;

    const lottieContainer = lottieContainerRef.current;
    if (!lottieContainer) return undefined;

    const anim = lottie.loadAnimation({
      container: lottieContainer,
      renderer: 'svg',
      loop: true,
      autoplay: false,
      animationData: orbitAnimationData,
    });

    animInstanceRef.current = anim;
    anim.addEventListener('enterFrame', handleEnterFrame);

    return () => {
      anim.removeEventListener('enterFrame', handleEnterFrame);
      anim.destroy();
      animInstanceRef.current = null;
      baselineRef.current = [];
    };
  }, [images.length, handleEnterFrame]);

  // Start orbit after entrance animation completes
  useEffect(() => {
    if (!isAnimating) {
      orbitActiveRef.current = false;
      animInstanceRef.current?.stop();
      baselineRef.current = [];
      // Remove orbit class
      imageRefs.current.forEach((el) => el?.classList.remove('orbit-active'));
      return undefined;
    }

    // Wait for entrance animation (500ms transition + 300ms max stagger)
    const timer = setTimeout(() => {
      orbitActiveRef.current = true;
      baselineRef.current = []; // Reset baseline to capture from current frame
      // Disable CSS transform transition for instant orbit updates
      imageRefs.current.forEach((el) => el?.classList.add('orbit-active'));
      animInstanceRef.current?.play();
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [isAnimating]);

  if (images.length === 0) {
    return null;
  }

  return (
    <StyledScatteredImages ref={containerRef}>
      {/* Off-screen lottie renderer — invisible but sized for proper SVG transform computation */}
      <div
        ref={lottieContainerRef}
        style={{
          position: 'absolute',
          width: `${LOTTIE_W}px`,
          height: `${LOTTIE_H}px`,
          overflow: 'hidden',
          pointerEvents: 'none',
          visibility: 'hidden',
        }}
      />
      {images.map((image, index) => {
        const position = calculateImagePosition(index, images.length, image.aspectRatioType);
        const size = getImageSize(image.aspectRatioType);
        const rotation = getRotationForCount(index, images.length);
        const shiftAmount = getShiftAmount(image.aspectRatioType);

        return (
          <StyledScatteredImage
            // eslint-disable-next-line react/no-array-index-key
            key={`${image.id}-${index}`}
            ref={(el) => {
              imageRefs.current[index] = el;
            }}
            top={position.top}
            bottom={position.bottom}
            left={position.left}
            right={position.right}
            width={size.width}
            height={size.height}
            zIndex={position.zIndex}
            delay={index * 100}
            isVisible={isAnimating}
            rotation={rotation}
            shiftX={shiftAmount}
            isLeftSide={position.isLeftSide}
          >
            <img src={image.url} alt="" loading="lazy" />
          </StyledScatteredImage>
        );
      })}
    </StyledScatteredImages>
  );
};

export default ScatteredImages;
